import { Container } from "unstated";
import axios from "axios";
import * as Cookies from "js-cookie";
import { toast } from "react-toastify";
import { track } from "../analytics";

export interface IAppState {
  name: string;
  mailingsEmail: string;
  canVote: boolean;
  hasVoted: boolean;
  proposal: {
    documentUrl: string;
    stats: {
      against: number;
      againstCount: number;
      againstShares: number;
      for: number;
      forCount: number;
      forShares: number;
    };
    summary: string;
  };
  user: {
    connections: Array<{
      id: number;
      name: string;
      status: string;
    }>;
    holdings: {
      holdings_with_proposals: Array<{
        price: number;
        share_count: number;
        symbol: string;
      }>;
      holdings_without_proposals: Array<{
        price: number;
        share_count: number;
        symbol: string;
      }>;
    };
    email: string;
    name: string;
    phone: string;
    support: boolean | any;
    votingPowerAmount: number;
  };
  navOpen: boolean;
}

export const initialState: IAppState = {
  name: "",
  mailingsEmail: "",
  canVote: false,
  hasVoted: false,
  proposal: {
    documentUrl: "",
    stats: {
      against: 0,
      againstCount: 0,
      againstShares: 0,
      for: 0,
      forCount: 0,
      forShares: 0
    },
    summary: ""
  },
  user: {
    connections: [{ id: 0, name: "", status: "" }],
    holdings: {
      holdings_with_proposals: [
        {
          price: 0,
          share_count: 0,
          symbol: ""
        }
      ],
      holdings_without_proposals: [
        {
          price: 0,
          share_count: 0,
          symbol: ""
        }
      ]
    },
    email: "",
    name: "",
    phone: "",
    support: null,
    votingPowerAmount: 0
  },
  navOpen: false
};

// TODO: We should eventually break this up into many containers
export class AppContainer extends Container<IAppState> {
  public state = initialState;

  public clearUser() {
    this.setState(prevState => ({
      user: {
        ...prevState.user,
        email: "",
        phone: ""
      }
    }));
  }

  public deleteConnection(id: number) {
    const url = `${process.env.REACT_APP_HOST_URL}/connections/${id}`;

    return axios
      .delete(url, {
        headers: {
          Authorization: `JWT ${Cookies.get("authToken")}`
        }
      })
      .then(response => {
        toast.success("Successfully removed connection.", {
          position: toast.POSITION.TOP_RIGHT
        });
      })
      .catch(error => {
        if (this.isUnauthorized(error.response.status)) {
          this.handleSignOut();
        }
        toast.error(
          "Oops! Something went wrong while trying to remove this connection.",
          {
            position: toast.POSITION.TOP_RIGHT
          }
        );
      });
  }

  public fetchConnections() {
    const url = `${process.env.REACT_APP_HOST_URL}/connections/`;

    return axios
      .get(url, {
        headers: {
          Authorization: `JWT ${Cookies.get("authToken")}`
        }
      })
      .then(response => {
        this.setState(prevState => ({
          user: {
            ...prevState.user,
            connections: response.data.connections
          }
        }));
      })
      .catch(error => {
        if (this.isUnauthorized(error.response.status)) {
          this.handleSignOut();
        }

        return toast.error(
          "An error occurred when trying to fetch your connections.",
          {
            position: toast.POSITION.TOP_RIGHT
          }
        );
      });
  }

  public fetchProposal() {
    const url = `${process.env.REACT_APP_HOST_URL}/proposals/1/`;
    const authHeader = Cookies.get("authToken")
      ? `JWT ${Cookies.get("authToken")}`
      : "None";

    return axios
      .get(url, {
        headers: { Authorization: authHeader }
      })
      .then(response => {
        this.setState(prevState => ({
          ...prevState,
          hasVoted: response.data.user ? response.data.user.has_voted : false,
          proposal: {
            documentUrl: response.data.proposal.document_url,
            stats: {
              against: response.data.proposal.stats.against_dollars,
              againstCount: response.data.proposal.stats.against_count,
              againstShares: response.data.proposal.stats.against_shares,
              for: response.data.proposal.stats.for_dollars,
              forCount: response.data.proposal.stats.for_count,
              forShares: response.data.proposal.stats.for_shares
            },
            summary: response.data.proposal.summary
          },
          user: {
            ...prevState.user,
            support: response.data.user ? response.data.user.support : null,
            votingPowerAmount: response.data.user
              ? response.data.user.current_value
              : 0
          }
        }));
      })
      .catch(error => {
        if (this.isUnauthorized(error.response.status)) {
          this.handleSignOut();
        }
        toast.error("Oops! Something went wrong.", {
          position: toast.POSITION.TOP_RIGHT
        });
      });
  }

  public fetchUser() {
    const url = `${process.env.REACT_APP_HOST_URL}/users/`;

    return axios
      .get(url, {
        headers: {
          Authorization: `JWT ${Cookies.get("authToken")}`
        }
      })
      .then(response => {
        this.setState(prevState => ({
          user: {
            ...prevState.user,
            email: response.data.email,
            name: response.data.name,
            phone: response.data.phone_number,
            holdings: response.data.holdings
          }
        }));
      })
      .catch(error => {
        if (this.isUnauthorized(error.response.status)) {
          this.handleSignOut();
        }
        toast.error("Oops! Something went wrong.", {
          position: toast.POSITION.TOP_RIGHT
        });
      });
  }

  public resendCode() {
    const url = this.state.user.phone
      ? `${process.env.REACT_APP_HOST_URL}/users/phone/`
      : `${process.env.REACT_APP_HOST_URL}/users/`;
    const payloadBody = () => {
      if (this.state.user.phone) {
        // https://gist.github.com/gilluminate/4599286
        let phoneNumber = this.state.user.phone;

        if (phoneNumber[0] === "1") {
          phoneNumber = phoneNumber.slice(1);
        }

        const normalizePhoneNumber = phoneNumber.replace(/\D/g, "");

        return {
          phone_number: normalizePhoneNumber
        };
      }

      return {
        email: this.state.user.email
      };
    };
    axios
      .post(url, payloadBody())
      .then(response => {
        track("Resend Confirmation Code", { status: "success" });
        toast.success("New code sent!", {
          position: toast.POSITION.TOP_RIGHT
        });
      })
      .catch(error => {
        track("Resend Confirmation Code", { status: "failed" });
        if (this.isUnauthorized(error.response.status)) {
          this.handleSignOut();
        }
        toast.error(
          "We were unable to send you a new code. Please try again.",
          {
            position: toast.POSITION.TOP_RIGHT
          }
        );
      });
  }

  public setCanVote(canVote: boolean) {
    this.setState(prevState => ({
      canVote
    }));
  }

  public setEmail(email: string) {
    this.setState(prevState => ({
      user: {
        ...prevState.user,
        email
      }
    }));
  }

  public setPhone(phone: string) {
    this.setState(prevState => ({
      user: {
        ...prevState.user,
        phone
      }
    }));
  }

  public setToken(authToken: string) {
    Cookies.set("authToken", authToken);
  }

  public signOut() {
    track("Sign Out");
    Cookies.remove("authToken");
    localStorage.clear();

    toast.success("You have signed out.", {
      position: toast.POSITION.TOP_RIGHT
    });

    this.setState({
      canVote: false,
      hasVoted: false,
      user: {
        connections: [{ id: 0, name: "", status: "" }],
        holdings: {
          holdings_with_proposals: [
            {
              price: 0,
              share_count: 0,
              symbol: ""
            }
          ],
          holdings_without_proposals: [
            {
              price: 0,
              share_count: 0,
              symbol: ""
            }
          ]
        },
        email: "",
        name: "",
        phone: "",
        support: null,
        votingPowerAmount: 0
      }
    });
  }

  public updateUser(email: string, name: string, phone: string) {
    const url = `${process.env.REACT_APP_HOST_URL}/users/update/`;

    let phoneNumber = phone;
    if (phoneNumber[0] === "1") {
      phoneNumber = phoneNumber.slice(1);
    }
    const normalizePhoneNumber = phoneNumber.replace(/\D/g, "");

    return axios
      .patch(
        url,
        {
          email,
          name,
          phone_number: phone
        },
        {
          headers: {
            Authorization: `JWT ${Cookies.get("authToken")}`
          }
        }
      )
      .then(response => {
        this.setState(prevState => ({
          ...prevState,
          user: {
            ...prevState.user,
            email,
            name,
            phone: normalizePhoneNumber
          }
        }));
        toast.success("Profile saved.", {
          position: toast.POSITION.TOP_RIGHT
        });
      })
      .catch(error => {
        if (this.isUnauthorized(error.response.status)) {
          this.handleSignOut();
        }
        toast.error(
          error.response.data && error.response.data.error
            ? error.response.data.error
            : "Something went wrong updating your profile.",
          {
            position: toast.POSITION.TOP_RIGHT
          }
        );
      });
  }

  public vote(support: boolean) {
    const url = `${process.env.REACT_APP_HOST_URL}/proposals/1/`;

    return axios
      .post(
        url,
        { support },
        {
          headers: {
            Authorization: `JWT ${Cookies.get("authToken")}`
          }
        }
      )
      .then(response => {
        {
          this.setState(prevState => ({
            ...prevState,
            hasVoted: true,
            proposal: {
              ...prevState.proposal,
              stats: {
                against: response.data.stats.against_dollars,
                againstCount: response.data.stats.against_count,
                againstShares: response.data.stats.against_shares,
                for: response.data.stats.for_dollars,
                forCount: response.data.stats.for_count,
                forShares: response.data.stats.for_shares
              }
            },
            user: {
              ...prevState.user,
              support: response.data.support
            }
          }));
        }
      })
      .catch(error => {
        if (this.isUnauthorized(error.response.status)) {
          this.handleSignOut();
        }
        toast.error(
          "Oops! Something went wrong submitting your vote. Please try again.",
          {
            position: toast.POSITION.TOP_RIGHT
          }
        );
      });
  }

  public updateName(name: string) {
    this.setState(prevState => ({
      ...prevState,
      name
    }));
  }

  public setMailingsEmail(email: string) {
    this.setState(prevState => ({
      ...prevState,
      mailingsEmail: email
    }));
  }

  public setNavOpen = (navOpen: boolean) => {
    this.setState(prevState => ({
      ...prevState,
      navOpen
    }));
  };

  private isUnauthorized(responseCode: number) {
    return responseCode === 401 || responseCode === 403;
  }

  private handleSignOut() {
    track("Sign Out");
    Cookies.remove("authToken");
    localStorage.clear();

    toast.success("You have signed out.", {
      position: toast.POSITION.TOP_RIGHT
    });

    this.setState(initialState);
  }
}



// WEBPACK FOOTER //
// ./src/containers/App.container.ts